/** @jsxImportSource @emotion/react */
import { css, Theme, useTheme } from '@emotion/react';
import { FC } from 'react';
import { MdRemoveCircleOutline } from 'react-icons/md';
import { Trans, useTranslation } from 'react-i18next';

import { Flex, Box } from 'components/styled';
import Text from 'components/styled/Text';
import Button from 'components/styled/Button';
import Divider from 'components/styled/Divider';
import ModalDialog from 'components/modal';
import {
	CustomProductSections,
	descriptionData,
} from 'components/tiles/productTab/ProductsCustomData';

import { formatCzechCrowns } from 'utils';

import { ReactComponent as CheckmarkIcon } from 'assets/checkmark.svg';

import { Backend } from 'api/endpoints';

import { useLocalizedMultistring } from 'utils/localization';
import { LicenseTypeById, ProductTypeById } from 'utils/enumMaps';

import ProductEdit from './ProductEdit';
import {
	CustomerServiceDialog,
	AdditionalPackagesMoreInfoDialog,
} from './MoreInfoDialog';

type CommonProps = {
	product: Backend.Product;
	isSelected?: boolean;
	productTypeById: ProductTypeById;
};

type EditProps = {
	variant: 'EDIT';
};

type PreviewProps = {
	variant: 'PREVIEW';
};

type OrderProps = {
	variant: 'ORDER';
	disabled?: boolean;
	onSelect: (p: Backend.Product) => void;
	onDeselect?: () => void;
};

const isUnlimited = (count: number) => count <= -1 || count > 9999;

type Props = CommonProps & (EditProps | OrderProps | PreviewProps);

/*TODO: Refactor */

const ProductTab: FC<Props> = ({
	product,
	isSelected,
	productTypeById,
	...p
}) => {
	const { t, i18n } = useTranslation('components/tiles/product_tab/index/');
	const localize = useLocalizedMultistring();

	const { university } = useTheme();
	const productDescription = descriptionData[university];

	const isLicense = product.productType === 'LICENSE';
	const isOffers = product.productType === 'OFFERS';
	const isReveals = product.productType === 'REVEALS';

	// avoid crash, when product ID is not recognized
	if (productTypeById === undefined) {
		return null;
	}

	return (
		<Flex
			py={4}
			flex={1}
			flexDirection="column"
			alignItems="stretch"
			textAlign="center"
			css={css`
				box-shadow: ${isSelected
					? '8px 8px 4px rgba(16, 19, 120, 0.15)'
					: '0px 4px 4px rgba(0, 0, 0, 0.1)'};
				border: ${isSelected ? '1px solid rgba(16, 19, 120, 0.2)' : 0};
			`}
		>
			<Flex flexDirection="column">
				{/* Header */}
				<Flex alignItems="center" flexDirection="column" px={4}>
					<Text fontSize="lg" color="primary" fontWeight="bold">
						{localize(product.name)}
						{university === 'ZLIN' && (isOffers || isReveals) && (
							<AdditionalPackagesMoreInfoDialog
								isOffers={isOffers}
								isReveals={isReveals}
							/>
						)}
						<Divider as="span" display="block" mt={3} />
					</Text>
				</Flex>
				{/* Price */}
				<Text fontWeight="bolder" fontSize="xl" color="secondary" px={4} mb={0}>
					{formatCzechCrowns(product.priceWithoutTax, i18n.language)}
				</Text>
				<Text fontSize="sm" color="primary" mt={0}>
					{t('without_vat')}
				</Text>
				{(isLicense ||
					(university === 'ZLIN' &&
						isOffers &&
						product.offersCount === -1)) && (
					<Text fontSize="sm" color="textLight" my={0} pb={3} px={4}>
						{t('price_for_one_year')}
					</Text>
				)}
				{/* Description */}
				<Flex
					flexDirection="column"
					css={(theme: Theme) => css`
						& div:nth-of-type(odd) {
							background-color: ${theme.colors.lightGrey};
						}
					`}
				>
					{university === 'ZLIN' && isLicense && <CustomerServiceDialog />}

					{isLicense &&
						productDescription[productTypeById as LicenseTypeById].map(d => (
							<Flex key={localize(d)} justifyContent="center" px={4}>
								<Trans>
									<Text>{localize(d)}</Text>
								</Trans>
							</Flex>
						))}

					{/*LINCESE: ZLIN */}
					{university === 'ZLIN' &&
						productTypeById === 'LICENSE_COMPLETE' &&
						product.offersCount !== 0 && (
							<Flex justifyContent="center">
								{isUnlimited(product.offersCount) ? (
									<Text px={4}>
										<Text fontWeight="bold" as="span">
											{t('unlimited')}
										</Text>
										{t('unlimited_offers')}
									</Text>
								) : (
									<Text px={4}>
										{t('limited_offers', {
											offersCount: product.offersCount.toString(),
										})}
									</Text>
								)}
							</Flex>
						)}

					{/* OFFERS: for MUNI and LIBEREC, display it both in licences
					as well as in additional packages.
					For PLZEN display this info only in licences, but not in
					additional packages. */}
					{product.offersCount !== 0 &&
						university !== 'ZLIN' &&
						(university !== 'PLZEN' || isLicense) && (
							<Flex justifyContent="center">
								{isUnlimited(product.offersCount) ? (
									<Text px={4}>
										<Text fontWeight="bold" as="span">
											{t('unlimited')}
										</Text>
										{t('unlimited_offers')}
									</Text>
								) : (
									<Text px={4}>
										{t('limited_offers', {
											offersCount: product.offersCount.toString(),
										})}
									</Text>
								)}
							</Flex>
						)}

					{/* OFFERS: ZLIN */}
					{isOffers && product.offersCount !== 0 && university === 'ZLIN' && (
						<Flex justifyContent="center">
							{isUnlimited(product.offersCount) ? (
								<Text px={4}>{t('unlimited_offers_ZLIN')}</Text>
							) : (
								<Text px={4}>{t('limited_offers_ZLIN')}</Text>
							)}
						</Flex>
					)}

					{isOffers && product.offersCount !== 0 && university === 'ZLIN' && (
						<Flex justifyContent="center">
							<Trans>
								<Text px={4}>{t('limited_offers_visibility_ZLIN')}</Text>
							</Trans>
						</Flex>
					)}

					{/* OFFERS: PLZEN */}
					{isOffers && product.offersCount !== 0 && university === 'PLZEN' && (
						<Flex justifyContent="center">
							{isUnlimited(product.offersCount) ? (
								<Text px={4}>{t('unlimited_offers_PLZEN')}</Text>
							) : (
								<Text px={4}>{t('one_offer_PLZEN')}</Text>
							)}
						</Flex>
					)}

					{/* REVEALS: MUNI, LIBEREC, PLZEN */}
					{product.revealsCount !== 0 && university !== 'ZLIN' && (
						<Flex justifyContent="center">
							{isUnlimited(product.revealsCount) ? (
								<Text px={4}>
									<Text fontWeight="bold" as="span">
										{t('unlimited')}
									</Text>{' '}
									{t('unlimited_annonymous_reveals')}
								</Text>
							) : (
								<Text px={4}>
									{t('anonymous_reveals', {
										revealsCount: product.revealsCount.toString(),
									})}
								</Text>
							)}
						</Flex>
					)}

					{/* REVEALS (license): ZLIN */}
					{product.revealsCount !== 0 && isLicense && university === 'ZLIN' && (
						<Flex justifyContent="center">
							{isUnlimited(product.revealsCount) ? (
								<Text px={4}>
									<Text fontWeight="bold" as="span">
										{t('unlimited')}
									</Text>{' '}
									{t('unlimited_annonymous_reveals')}
								</Text>
							) : (
								<Text px={4}>
									<Trans>
										{t('anonymous_reveals_ZLIN', {
											revealsCount: product.revealsCount.toString(),
										})}
									</Trans>
								</Text>
							)}
						</Flex>
					)}

					{/* REVEALS (additional packages): ZLIN */}
					{isReveals && product.revealsCount !== 0 && university === 'ZLIN' && (
						<Flex justifyContent="center">
							{isUnlimited(product.revealsCount) ? (
								<Text px={4}>{t('unlimited_reveals_ZLIN')}</Text>
							) : (
								<Text px={4}>{t('limited_reveals_ZLIN')}</Text>
							)}
						</Flex>
					)}
				</Flex>
			</Flex>
			<Flex flexGrow={1} my={3} />
			<Flex px={4}>
				<CustomProductSections
					product={product}
					licenseType={productTypeById as LicenseTypeById}
					university={university}
					isLicense={isLicense}
				/>
			</Flex>

			<Flex flexDirection="column">
				{/* Buttons */}
				{p.variant === 'ORDER' && (
					<Flex
						flexDirection="column"
						justifyContent="center"
						alignItems="center"
						gap={3}
						mt={3}
					>
						{isSelected ? (
							<Button disabled={p.disabled} variant="primary">
								<CheckmarkIcon width={13} />{' '}
								<Text pl={2} as="span">
									{t('selected')}
								</Text>
							</Button>
						) : (
							<Button
								onClick={() => p.onSelect(product)}
								disabled={p.disabled}
								variant="outlined"
							>
								{t('select_package')}
							</Button>
						)}

						{isSelected && p.onDeselect && (
							<Button variant="outlined" onClick={p.onDeselect}>
								<Box mr={2}>
									<MdRemoveCircleOutline />
								</Box>
								{t('remove')}
							</Button>
						)}
					</Flex>
				)}

				<Flex flexGrow={1} my={3} />
				{p.variant === 'EDIT' && (
					<ModalDialog
						label={t('edit_package_label')}
						control={openModal => (
							<Button m="auto" variant="primary" onClick={openModal} mt={3}>
								<Text as="span">{t('edit_package')}</Text>
							</Button>
						)}
					>
						{closeModal => (
							<ProductEdit product={product} closeModal={closeModal} />
						)}
					</ModalDialog>
				)}
			</Flex>
		</Flex>
	);
};

export default ProductTab;
